import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { appeler } from '../../api/client.ts';
import { Chargement, Etiquette, Vide } from '../../composants/communs.tsx';
import { formaterFcfa } from '../../composants/ui/index.ts';
import { CANAUX, LIBELLE_CANAL, type Canal } from '../produits/types.ts';
import type { ProduitVendable } from './vente-types.ts';

/**
 * Seuils d'affichage du stock — aucune règle serveur ne les fixe encore :
 * en dessous de 6, « Plus que N » prévient que ça presse ; au-dessus,
 * « Stock N » suffit. À ajuster si une vraie politique de réassort l'exige.
 */
const SEUIL_STOCK_BAS = 6;

/**
 * Recherche et sélection des produits.
 *
 * La grille s'affiche dès l'arrivée sur l'écran (tout le canal, vendable),
 * la recherche filtre en plus — décision du 04/10/2026, à l'inverse du
 * choix précédent de ne rien montrer avant une frappe : au comptoir, voir
 * les articles courants d'un coup d'œil vaut mieux qu'un champ vide.
 */
export function PanierProduits({
  boutiqueId,
  canal,
  onCanal,
  onAjouter,
  quantitesPanier,
}: {
  boutiqueId: string;
  canal: Canal;
  onCanal: (canal: Canal) => void;
  onAjouter: (produit: ProduitVendable) => void;
  /** Quantité déjà au panier par produit — affiche le badge « ×N » sur sa carte. */
  quantitesPanier: Record<string, number>;
}): ReactNode {
  const [recherche, setRecherche] = useState('');

  const terme = recherche.trim();

  const { data: resultats, isFetching } = useQuery({
    queryKey: ['vente-produits', boutiqueId, canal, terme],
    queryFn: () =>
      appeler<{ donnees: ProduitVendable[] }>(
        // `q` absent (pas vide) au repos : le serveur rejette une chaîne
        // vide (`min(1)`), seul un paramètre ABSENT vaut « tout le canal ».
        `/produits?canal=${canal}&boutique_id=${boutiqueId}` +
          (terme === '' ? '' : `&q=${encodeURIComponent(terme)}`),
      ),
    // Le serveur trie par désignation, sans distinguer ce qui se vend de ce
    // qui ne se vend pas : au comptoir, une rupture mélangée au reste fait
    // perdre du temps à cliquer dessus pour rien. Tri STABLE — seule la
    // rupture déplace une carte, l'ordre alphabétique de chaque groupe reste
    // celui du serveur.
    select: (reponse) =>
      [...reponse.donnees].sort(
        (a, b) => Number(a.stockDisponible <= 0) - Number(b.stockDisponible <= 0),
      ),
  });

  return (
    <div className="vente-section">
      <div className="vente-section__tete">
        <h2>Ajouter un article</h2>
        <div className="segmente">
          {CANAUX.map((valeur) => (
            <button
              key={valeur}
              type="button"
              className={`segmente__option${canal === valeur ? ' segmente__option--actif' : ''}`}
              onClick={() => {
                onCanal(valeur);
              }}
            >
              {LIBELLE_CANAL[valeur]}
            </button>
          ))}
        </div>
      </div>

      <input
        placeholder="Scanner le code-barres ou taper une référence, une désignation…"
        value={recherche}
        onChange={(evenement) => {
          setRecherche(evenement.target.value);
        }}
      />
      <p className="entete__sous" style={{ marginTop: 4 }}>
        Astuce : un scan ajoute directement l’article au panier.
      </p>

      {isFetching && <Chargement />}
      {!isFetching && (resultats?.length ?? 0) === 0 && (
        <Vide
          texte={
            recherche.trim() === ''
              ? 'Aucun produit vendable sur ce canal.'
              : 'Aucun produit vendable pour cette recherche, sur ce canal.'
          }
        />
      )}

      {(resultats?.length ?? 0) > 0 && (
        <div className="grille-produits-vente">
          {resultats?.map((produit) => {
            const dejaAuPanier = quantitesPanier[produit.id] ?? 0;
            const rupture = produit.stockDisponible <= 0;
            return (
              <button
                key={produit.id}
                type="button"
                className={`carte-produit${dejaAuPanier > 0 ? ' carte-produit--au-panier' : ''}`}
                disabled={rupture}
                title={rupture ? 'Rupture de stock : cette vente serait refusée par le serveur (RG-048).' : undefined}
                onClick={() => {
                  onAjouter(produit);
                }}
              >
                {dejaAuPanier > 0 && <span className="carte-produit__quantite">×{dejaAuPanier}</span>}
                <div>{produit.designation}</div>
                <div className="carte-produit__reference">{produit.reference}</div>
                <div className="carte-produit__pied">
                  <span className="carte-produit__prix">{formaterFcfa(produit.prixUnitaire)}</span>
                  {rupture ? (
                    <Etiquette ton="rouge" texte="Rupture" />
                  ) : produit.stockDisponible <= SEUIL_STOCK_BAS ? (
                    <Etiquette ton="ambre" texte={`Plus que ${String(produit.stockDisponible)}`} />
                  ) : (
                    <Etiquette ton="vert" texte={`Stock ${String(produit.stockDisponible)}`} />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
